/**
 * F-148 §10.2 line card (AC4, AC7–AC12, AC31). Single column at every width. Shows the image,
 * product name and exact variant in `<bdi>`, identifiers in `<bdi dir="ltr">`, the unit/pack
 * choice, pack maths with confirmation, the 2×2 quantity grid and per-line issues. Conflicts
 * render the side-by-side panel; unresolved scans show a badge. Highlight scrolls the card into
 * view for 1.5 s (no animation under `prefers-reduced-motion`).
 */
import { useEffect, useId, useRef } from 'react';
import { ImageOff, Loader2, ScanLine, Trash2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  RECEIVING_LIMITS,
  type ReceivingLineIssue,
  type ReceivingQuantityField,
  type ReceivingUnitKind,
  type ReceivingVariantSnapshot,
} from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { EditorLine, IdentifierField } from '../lib/draft-reducer';
import { issueMessageKey } from '../lib/error-messages';
import { IdentifierConflictPanel } from './identifier-conflict-panel';
import { LineQuantityGrid } from './line-quantity-grid';
import { PackConversionField } from './pack-conversion-field';

export interface ReceivingLineCardProps {
  readonly line: EditorLine;
  readonly issues: readonly ReceivingLineIssue[];
  /** Identifiers are waiting for server resolution (no identifier issue shown yet). */
  readonly checking?: boolean;
  readonly highlighted?: boolean;
  readonly disabled?: boolean;
  readonly imageUrlFor?: (path: string) => string | null;
  readonly onEditQuantity: (field: ReceivingQuantityField, value: number | null) => void;
  readonly onSetUnitKind: (unitKind: ReceivingUnitKind) => void;
  readonly onConfirmPack: (confirmed: boolean) => void;
  readonly onSelectVariant: () => void;
  readonly onClearVariant: () => void;
  readonly onResolveConflict: (variant: ReceivingVariantSnapshot) => void;
  readonly onSetIdentifier: (field: IdentifierField, value: string | null) => void;
  readonly onSetNote: (note: string | null) => void;
  readonly onRemove: () => void;
}

const GRID_FIELDS: ReadonlySet<string> = new Set([
  'expectedUnits',
  'acceptedUnits',
  'damagedUnits',
  'missingUnits',
  'packsExpected',
  'unitsPerPack',
  'unitsPerPackConfirmed',
]);

const IDENTIFIER_FIELDS: readonly {
  field: IdentifierField;
  labelKey:
    | 'admin.receiving.line.barcode'
    | 'admin.receiving.line.variantRef'
    | 'admin.receiving.line.supplierCode'
    | 'admin.receiving.line.supplierItem';
}[] = [
  { field: 'barcode', labelKey: 'admin.receiving.line.barcode' },
  { field: 'variantRef', labelKey: 'admin.receiving.line.variantRef' },
  { field: 'supplierCode', labelKey: 'admin.receiving.line.supplierCode' },
  { field: 'supplierItemName', labelKey: 'admin.receiving.line.supplierItem' },
];

export function ReceivingLineCard({
  line,
  issues,
  checking = false,
  highlighted = false,
  disabled = false,
  imageUrlFor,
  onEditQuantity,
  onSetUnitKind,
  onConfirmPack,
  onSelectVariant,
  onClearVariant,
  onResolveConflict,
  onSetIdentifier,
  onSetNote,
  onRemove,
}: ReceivingLineCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!highlighted || !rootRef.current) return;
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof rootRef.current.scrollIntoView === 'function') {
      rootRef.current.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }
  }, [highlighted]);

  const variant = line.variant;
  const imageSrc = variant?.imagePath && imageUrlFor ? imageUrlFor(variant.imagePath) : null;
  const cardIssues = issues.filter((issue) => !issue.field || !GRID_FIELDS.has(issue.field));
  const hasConflict = issues.some((issue) => issue.code === 'IDENTIFIER_CONFLICT');
  const isScanUnresolved = line.source === 'scan' && line.variantId === null;
  const hasIdentifiers =
    line.identifiers.barcode !== null ||
    line.identifiers.variantRef !== null ||
    line.identifiers.supplierCode !== null ||
    line.identifiers.supplierItemName !== null;

  return (
    <article
      ref={rootRef}
      className={cn(
        'border-border bg-card shadow-admin-card space-y-4 rounded-lg border p-4 text-start transition-shadow',
        highlighted ? 'ring-admin-lime ring-2 ring-offset-2' : '',
        cardIssues.length > 0 || issues.length > 0
          ? 'border-amber-300 dark:border-amber-500/40'
          : '',
      )}
      aria-labelledby={`${id}-title`}
      tabIndex={-1}
      data-testid="receiving-line-card"
      data-client-line-id={line.clientLineId}
      data-line-no={line.lineNo}
      data-highlighted={highlighted ? 'true' : undefined}
    >
      <header className="flex items-center justify-between gap-2">
        <h3
          id={`${id}-title`}
          className="text-muted-foreground text-xs font-semibold uppercase tracking-wide"
        >
          {t('admin.receiving.line.lineNo', { line: line.lineNo })}
          {line.source === 'csv' && line.sourceRowNumber !== null ? (
            <span className="ms-2 font-mono normal-case">
              <bdi dir="ltr">CSV {line.sourceRowNumber}</bdi>
            </span>
          ) : null}
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11"
          disabled={disabled}
          onClick={onRemove}
          aria-label={t('admin.receiving.line.remove')}
          data-testid="line-remove"
        >
          <Trash2 aria-hidden="true" className="size-4" />
        </Button>
      </header>

      {/* Variant block */}
      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-start gap-3 sm:grid-cols-[3.5rem_minmax(0,1fr)_auto]">
        <div className="bg-admin-surface-muted flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md">
          {imageSrc ? (
            <img src={imageSrc} alt="" className="size-full object-cover" loading="lazy" />
          ) : (
            <ImageOff
              aria-hidden="true"
              className="text-muted-foreground size-5"
              strokeWidth={1.5}
            />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          {variant ? (
            <>
              <p
                className="text-foreground text-base font-semibold leading-tight"
                data-testid="line-product-name"
              >
                <bdi>{variant.productName}</bdi>
              </p>
              <p className="text-muted-foreground text-sm" data-testid="line-variant-summary">
                <span className="sr-only">{t('admin.receiving.line.variant')}: </span>
                <bdi>{variant.optionSummary || '—'}</bdi>
              </p>
              {/* `Badge` renders a div, so this row is a div (not a p) to keep the DOM valid. */}
              <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                <span>
                  {t('admin.barcode.result.stock')}: <bdi dir="ltr">{variant.inventory}</bdi>
                </span>
                {!variant.active ? (
                  <Badge variant="neutral" data-testid="line-inactive-badge">
                    {t('admin.receiving.line.inactiveBadge')}
                  </Badge>
                ) : null}
                {!variant.eligible ? (
                  <Badge variant="destructive">{t(issueMessageKey('VARIANT_INELIGIBLE'))}</Badge>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <p className="text-foreground text-sm font-medium" data-testid="line-no-variant">
                {checking ? (
                  <span className="text-muted-foreground inline-flex items-center gap-2">
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin motion-reduce:animate-none"
                    />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('admin.receiving.line.noVariant')
                )}
              </p>
              {isScanUnresolved ? (
                <Badge variant="warning" data-testid="line-unresolved-scan">
                  <ScanLine aria-hidden="true" className="me-1 size-3" />
                  {t('admin.receiving.line.unresolvedScan')}
                </Badge>
              ) : null}
            </>
          )}
          {hasIdentifiers ? (
            <p className="text-muted-foreground font-mono text-xs" data-testid="line-identifiers">
              {line.identifiers.barcode ? <bdi dir="ltr">{line.identifiers.barcode}</bdi> : null}
              {line.identifiers.variantRef ? (
                <>
                  {line.identifiers.barcode ? ' · ' : ''}
                  <bdi dir="ltr">{line.identifiers.variantRef}</bdi>
                </>
              ) : null}
              {line.identifiers.supplierCode ? (
                <>
                  {line.identifiers.barcode || line.identifiers.variantRef ? ' · ' : ''}
                  <bdi dir="ltr">{line.identifiers.supplierCode}</bdi>
                </>
              ) : null}
              {line.identifiers.supplierItemName ? (
                <>
                  {' · '}
                  <bdi>{line.identifiers.supplierItemName}</bdi>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
        <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col">
          <Button
            type="button"
            variant={variant ? 'outline' : 'default'}
            className="min-h-11"
            disabled={disabled}
            onClick={onSelectVariant}
            data-testid="line-select-variant"
          >
            {variant
              ? t('admin.receiving.line.changeVariant')
              : t('admin.receiving.line.selectVariant')}
          </Button>
          {variant ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={onClearVariant}
              data-testid="line-clear-variant"
            >
              {t('admin.receiving.line.clearVariant')}
            </Button>
          ) : null}
        </div>
      </div>

      {hasConflict ? (
        <IdentifierConflictPanel
          byBarcode={
            line.candidates.byBarcode.find((c) => c.eligible) ??
            line.candidates.byBarcode[0] ??
            null
          }
          byRef={line.candidates.byVariantRef}
          onResolve={onResolveConflict}
          disabled={disabled}
        />
      ) : null}

      {/* Unit kind */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium">{t('admin.receiving.line.unitKind')}</legend>
        <div
          role="radiogroup"
          className="grid grid-cols-2 gap-2"
          aria-label={t('admin.receiving.line.unitKind')}
        >
          {(['unit', 'pack'] as const).map((kind) => {
            const radioId = `${id}-kind-${kind}`;
            const selected = line.unitKind === kind;
            return (
              <label
                key={kind}
                htmlFor={radioId}
                className={cn(
                  // The radio itself is `sr-only`; the label carries the visible focus ring.
                  'has-[:focus-visible]:ring-ring/50 flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm has-[:focus-visible]:ring-2',
                  selected
                    ? 'border-admin-lime bg-admin-lime-light/40 font-medium'
                    : 'border-input bg-card',
                  disabled ? 'cursor-not-allowed opacity-60' : '',
                )}
              >
                <input
                  id={radioId}
                  type="radio"
                  name={`${id}-kind`}
                  value={kind}
                  checked={selected}
                  disabled={disabled}
                  onChange={() => onSetUnitKind(kind)}
                  className="sr-only"
                  data-testid={`line-unit-kind-${kind}`}
                />
                <span>
                  {kind === 'unit'
                    ? t('admin.receiving.line.unit')
                    : t('admin.receiving.line.pack')}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {line.unitKind === 'pack' ? (
        <PackConversionField
          packsExpected={line.packsExpected}
          unitsPerPack={line.unitsPerPack}
          unitsPerPackConfirmed={line.unitsPerPackConfirmed}
          issues={issues}
          disabled={disabled}
          onEdit={onEditQuantity}
          onConfirm={onConfirmPack}
        />
      ) : null}

      <LineQuantityGrid
        expectedUnits={line.expectedUnits}
        acceptedUnits={line.acceptedUnits}
        damagedUnits={line.damagedUnits}
        missingUnits={line.missingUnits}
        acceptedAuto={line.acceptedAuto}
        expectedReadOnly={line.unitKind === 'pack'}
        issues={issues}
        disabled={disabled}
        onEdit={onEditQuantity}
      />

      {/* Identifiers + note */}
      <details className="group" data-testid="line-details">
        <summary className="text-muted-foreground flex min-h-11 cursor-pointer list-none items-center text-xs font-medium underline-offset-4 hover:underline">
          {t('admin.receiving.line.identifiers')} / {t('admin.receiving.line.note')}
        </summary>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {IDENTIFIER_FIELDS.map(({ field, labelKey }) => {
            const inputId = `${id}-${field}`;
            const ltr = field !== 'supplierItemName';
            return (
              <div key={field} className="space-y-1">
                <Label htmlFor={inputId} className="text-xs">
                  {t(labelKey)}
                </Label>
                <Input
                  id={inputId}
                  value={line.identifiers[field] ?? ''}
                  onChange={(event) => onSetIdentifier(field, event.target.value)}
                  disabled={disabled}
                  autoComplete="off"
                  dir={ltr ? 'ltr' : undefined}
                  maxLength={RECEIVING_LIMITS.maxTextField}
                  className={cn('min-h-11 text-start', ltr ? 'font-mono' : '')}
                  data-testid={`line-identifier-${field}`}
                />
              </div>
            );
          })}
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${id}-note`} className="text-xs">
              {t('admin.receiving.line.note')}
            </Label>
            <Input
              id={`${id}-note`}
              value={line.note ?? ''}
              onChange={(event) => onSetNote(event.target.value)}
              disabled={disabled}
              maxLength={RECEIVING_LIMITS.maxNote}
              className="min-h-11 text-start"
              data-testid="line-note"
            />
          </div>
        </div>
      </details>

      {cardIssues.length > 0 ? (
        <ul className="space-y-1" data-testid="line-issues">
          {cardIssues.map((issue, index) => (
            <li
              key={`${issue.code}-${issue.field ?? ''}-${index}`}
              className="text-destructive text-xs"
              data-code={issue.code}
            >
              {t(issueMessageKey(issue.code), { ...issue.meta, max: RECEIVING_LIMITS.maxQuantity })}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
